<script setup lang="ts">
import type { TreeBulkSectionSelectionState } from "@/app/tools/new-section-management/site-sections-tree/bulk-category-assignment/bulk-category-selection";
import type { SiteSectionsTreeSectionCategoryFilterOption } from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter.types";
import type { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import type { V4HSiteId, SiteSectionCategoryId, SiteSectionId } from "@/data/common/ids";
import type { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import type { IVirtualizedTreeNode } from "@voltalis/ui-kit/components";

import { SectionAppliancesCountByProgrammingCategory, SiteTreeStructure } from "@voltalis/ui-kit/components";
import { CubeIcon } from "@voltalis/ui-kit/icons";
import { MarkerPinIcon } from "@voltalis/ui-kit/icons";
import { isEmptyObject } from "es-toolkit";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { countAllLeafDescendants } from "@/utils/site-section/site-section-utils";

import Skeleton from "primevue/skeleton";

import BulkSectionCheckbox from "@/app/tools/new-section-management/site-sections-tree/bulk-category-assignment/bulk-section-checkbox.vue";
import SiteSectionsTreeDisplayHeader from "@/app/tools/new-section-management/site-sections-tree/tree-display/site-sections-tree-display-header.vue";

type TreeNode = IVirtualizedTreeNode<TSiteSectionsTreeData>;

const props = defineProps<{
  applianceFilterSiteSectionsTree?: TreeNode;
  areAllVisibleSectionsSelected: boolean;
  areVisibleSectionsPartiallySelected: boolean;
  categoryOptions: readonly SiteSectionsTreeSectionCategoryFilterOption[];
  createCategory: (name: string) => Promise<boolean>;
  getSectionSelectionState: (section: TreeNode) => TreeBulkSectionSelectionState;
  hideRootNode: boolean;
  isCreatingCategory: boolean;
  isLoading: boolean;
  isSectionPreviewed: (sectionId: SiteSectionId) => boolean;
  siteId: V4HSiteId;
  siteSectionsTree?: TreeNode;
  visibleSectionsCountLabel: string;
}>();

const searchQuery = defineModel<string>("searchQuery", { required: true });
const selectedApplianceCategories = defineModel<EProgramApplianceCategory[]>("selectedApplianceCategories", {
  required: true,
});
const selectedSectionCategories = defineModel<SiteSectionCategoryId[]>("selectedSectionCategories", { required: true });

const emit = defineEmits<{
  (event: "preview-section-selection", section: TreeNode): void;
  (event: "remove-appliance-category", category: EProgramApplianceCategory): void;
  (event: "remove-section-category", categoryId: SiteSectionCategoryId): void;
  (event: "reset-section-selection-preview"): void;
  (event: "toggle-section-card-selection", section: TreeNode): void;
  (event: "toggle-visible-sections-selection", isSelected: boolean): void;
  (event: "update-section-selection", section: TreeNode, isSelected: boolean): void;
}>();
</script>

<template>
  <SiteTreeStructure
    :is-loading="props.isLoading"
    :siteSectionsTree="props.siteSectionsTree"
    :data-test-id="DATA_TEST_IDS.views.siteSectionTree.siteStructure"
    :draggable="false"
    :hideRootNode="props.hideRootNode"
  >
    <template #header>
      <SiteSectionsTreeDisplayHeader
        v-model:search-query="searchQuery"
        v-model:selected-appliance-categories="selectedApplianceCategories"
        v-model:selected-section-categories="selectedSectionCategories"
        :are-all-visible-sections-selected="props.areAllVisibleSectionsSelected"
        :are-visible-sections-partially-selected="props.areVisibleSectionsPartiallySelected"
        :category-options="props.categoryOptions"
        :create-category="props.createCategory"
        :is-creating-category="props.isCreatingCategory"
        :site-sections-tree="props.applianceFilterSiteSectionsTree"
        :visible-sections-count-label="props.visibleSectionsCountLabel"
        show-select-all
        @remove-appliance-category="emit('remove-appliance-category', $event)"
        @remove-section-category="emit('remove-section-category', $event)"
        @toggle-visible-sections-selection="emit('toggle-visible-sections-selection', $event)"
      />
    </template>

    <template #loader>
      <Skeleton
        class="!h-full"
        :data-test-id="DATA_TEST_IDS.views.siteSectionTree.skeleton"
      />
    </template>

    <template #nodeIcon="{ section }">
      <BulkSectionCheckbox
        v-if="section.data"
        :section="section"
        :selection-state="props.getSectionSelectionState(section)"
        :is-previewed="props.isSectionPreviewed(section.data.id)"
        @preview="emit('preview-section-selection', $event)"
        @reset-preview="emit('reset-section-selection-preview')"
        @update-selection="(targetSection, isSelected) => emit('update-section-selection', targetSection, isSelected)"
      />
      <MarkerPinIcon
        v-if="section.data === null"
        class="shrink-0"
      />
      <CubeIcon
        v-else-if="!section.data"
        class="shrink-0"
      />
    </template>

    <template #sectionName="{ section }">
      <div
        v-if="section.data === null"
        class="flex items-center w-full h-full justify-between group"
        :data-test-id="DATA_TEST_IDS.views.siteSectionTree.siteCard.card"
        :data-site-id="props.siteId"
      >
        <span
          class="font-medium text-base text-gray-700 overflow-hidden whitespace-nowrap text-ellipsis"
          :data-test-id="DATA_TEST_IDS.views.siteSectionTree.siteCard.siteName"
        >
          {{ section.name }}
        </span>
      </div>
      <div
        v-else
        class="flex items-center w-full h-full justify-between group cursor-pointer"
        :data-section-id="section.data?.id"
        :data-test-id="DATA_TEST_IDS.views.siteSectionTree.sectionCard.card"
        role="checkbox"
        :aria-checked="
          props.getSectionSelectionState(section).isIndeterminate
            ? 'mixed'
            : props.getSectionSelectionState(section).isSelected
        "
        tabindex="0"
        @click="emit('toggle-section-card-selection', section)"
        @keydown.enter.prevent="emit('toggle-section-card-selection', section)"
        @keydown.space.prevent="emit('toggle-section-card-selection', section)"
      >
        <div class="flex flex-col min-w-0">
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="font-medium text-base text-gray-700 overflow-hidden whitespace-nowrap text-ellipsis"
              :data-test-id="DATA_TEST_IDS.views.siteSectionTree.sectionCard.sectionName"
            >
              {{ section.name }}
            </span>

            <span
              v-if="section.parentsIndexes.length && section.children.length"
              class="text-gray-400 font-semibold"
              :data-test-id="DATA_TEST_IDS.views.siteSectionTree.sectionCard.sectionChildrenCount"
            >
              ({{ countAllLeafDescendants(section) }})
            </span>

            <span
              v-if="section.data.category"
              class="flex shrink-0 items-center justify-center gap-0.5 rounded-xl bg-gray-100 px-2 py-1 text-xs font-semibold not-italic leading-normal text-gray-500"
              :data-test-id="DATA_TEST_IDS.views.siteSectionTree.sectionCard.sectionCategory"
            >
              {{ section.data.category.name }}
            </span>
          </div>
          <SectionAppliancesCountByProgrammingCategory
            v-if="section.data && !isEmptyObject(section.data.applianceCountByCategory)"
            :applianceCountByProgrammingCategory="section.data.applianceCountByCategory"
          />
        </div>
      </div>
    </template>
  </SiteTreeStructure>
</template>
